import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";
import { toast } from "sonner";

const ITEMS_PER_PAGE = 10;

/**
 * Build where clause for driver managers (level 5)
 */
const buildDriverManagerWhereClause = (
  enabledFilter = "all",
  searchTerm = "",
) => {
  const baseWhere = {
    conditions: [
      {
        attribute: "level",
        value: [5], // Driver managers level
      },
    ],
    conector: "AND",
  };

  // Add enabled condition
  if (enabledFilter !== "all") {
    const enabled = enabledFilter === "enabled";
    baseWhere.conditions.push({
      attribute: "enabled",
      value: enabled,
    });
  }

  // Add search condition
  if (searchTerm && searchTerm.trim()) {
    const searchValue = `%${searchTerm.trim()}%`;
    baseWhere.conditions.push({
      conector: "OR",
      conditions: [
        {
          attribute: "name",
          value: {
            condition: "ilike",
            value: searchValue,
          },
        },
        {
          attribute: "lastname",
          value: {
            condition: "ilike",
            value: searchValue,
          },
        },
        {
          attribute: "email",
          value: {
            condition: "ilike",
            value: searchValue,
          },
        },
        {
          attribute: "cellphone",
          value: {
            condition: "ilike",
            value: searchValue,
          },
        },
      ],
    });
  }

  return baseWhere;
};

/**
 * Hook to get driver managers with pagination and filters
 */
export const useDriverManagers = (
  enabledFilter = "all",
  searchTerm = "",
  currentPage = 1,
) => {
  const whereClause = buildDriverManagerWhereClause(enabledFilter, searchTerm);

  const params = {
    params:
      "id,name,lastname,email,cellphone,country_phone_code,photo,enabled,level,created_at",
    where: whereClause,
    page: currentPage,
    page_size: ITEMS_PER_PAGE,
    orderBy: "-id",
  };

  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["driverManagers", enabledFilter, searchTerm, currentPage],
    queryFn: async () => {
      const response = await apiClient.get(
        `/users${queryString ? `?${queryString}` : ""}`,
      );
      return {
        users: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get driver manager stats
 */
export const useDriverManagerStats = (
  enabledFilter = "all",
  searchTerm = "",
) => {
  const whereClause = buildDriverManagerWhereClause(enabledFilter, searchTerm);

  const params = {
    params: "id",
    where: whereClause,
  };

  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["driverManagerStats", enabledFilter, searchTerm],
    queryFn: async () => {
      const response = await apiClient.get(
        `/users${queryString ? `?${queryString}` : ""}`,
      );
      return {
        total: response?.pagination?.total || 0,
      };
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get a single driver manager by ID
 */
export const useDriverManagerDetail = (userId, initialData = null) => {
  return useQuery({
    queryKey: ["driverManager", userId],
    queryFn: async () => {
      const response = await apiClient.get(`/users/${userId}`, {
        params: {
          params:
            "id,name,lastname,email,cellphone,country_phone_code,photo,enabled,level,created_at,addresses",
        },
      });
      return response?.result || null;
    },
    enabled: !!userId,
    // Missing navigation data is not a fetched not-found result.
    initialData: initialData ?? undefined,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to create a driver manager
 */
export const useCreateDriverManager = () => {
  const { t } = useTranslation("delivery");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData) => {
      const response = await apiClient.post("/users", {
        ...userData,
        level: 5, // Driver manager level
      });
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["driverManagers"] });
      queryClient.invalidateQueries({ queryKey: ["driverManagerStats"] });

      toast.success(t("toasts.driverManagerCreatedTitle"), {
        description: t("toasts.driverManagerCreatedDescription", {
          name: `${data.result?.name || ""} ${data.result?.lastname || ""}`.trim(),
        }),
        duration: 5000,
      });
    },
    onError: (error) => {
      toast.error(t("toasts.driverManagerCreateFailedTitle"), {
        description:
          error.message || t("toasts.driverManagerCreateFailedDescription"),
        duration: 5000,
      });
    },
  });
};

/**
 * Hook to toggle driver manager status
 */
export const useToggleDriverManagerStatus = () => {
  const { t } = useTranslation("delivery");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, enabled }) => {
      const response = await apiClient.post(`/users/${userId}`, { enabled });
      return response;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["driverManagers"] });
      queryClient.invalidateQueries({
        queryKey: ["driverManager", variables.userId],
      });
      queryClient.invalidateQueries({ queryKey: ["driverManagerStats"] });

      toast.success(t("toasts.statusUpdatedTitle"), {
        description: t("toasts.driverManagerStatusUpdatedDescription", {
          status: variables.enabled
            ? t("toasts.groupStatusActivated")
            : t("toasts.groupStatusDeactivated"),
        }),
        duration: 3000,
      });
    },
    onError: (error) => {
      toast.error(t("toasts.updateFailedTitle"), {
        description:
          error.message ||
          t("toasts.driverManagerStatusUpdateFailedDescription"),
        duration: 3000,
      });
    },
  });
};

/**
 * Hook to update a driver manager
 */
export const useUpdateDriverManager = () => {
  const { t } = useTranslation("delivery");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, userData }) => {
      const response = await apiClient.post(`/users/${userId}`, userData);
      return response;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["driverManagers"] });
      queryClient.invalidateQueries({
        queryKey: ["driverManager", variables.userId],
      });
      queryClient.invalidateQueries({ queryKey: ["driverManagerStats"] });

      toast.success(t("toasts.driverManagerUpdatedTitle"), {
        description: t("toasts.driverManagerUpdatedDescription"),
        duration: 3000,
      });
    },
    onError: (error) => {
      toast.error(t("toasts.updateFailedTitle"), {
        description:
          error.message || t("toasts.driverManagerUpdateFailedDescription"),
        duration: 3000,
      });
    },
  });
};

/**
 * Hook to delete a driver manager
 */
export const useDeleteDriverManager = () => {
  const { t } = useTranslation("delivery");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId) => {
      const response = await apiClient.delete(`/users/${userId}`);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["driverManagers"] });
      queryClient.invalidateQueries({ queryKey: ["driverManagerStats"] });

      toast.success(t("toasts.driverManagerDeletedTitle"), {
        description: t("toasts.driverManagerDeletedDescription"),
        duration: 3000,
      });
    },
    onError: (error) => {
      toast.error(t("toasts.deletionFailedTitle"), {
        description:
          error.message || t("toasts.driverManagerDeleteFailedDescription"),
        duration: 3000,
      });
    },
  });
};

export default {
  useDriverManagers,
  useDriverManagerStats,
  useDriverManagerDetail,
  useCreateDriverManager,
  useUpdateDriverManager,
  useToggleDriverManagerStatus,
  useDeleteDriverManager,
};
